import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { DateTime } from 'luxon';

// Debug component import
console.log('SearchResults component imported');

// Time range configurations
const TIME_RANGES = {
  morning: { min: '06', max: '12' },
  afternoon: { min: '12', max: '18' },
  evening: { min: '18', max: '22' }
};

// Available courses with booking URLs
const COURSES = {
  '8': {
    id: '8',
    name: 'Seymour Golf and Country Club',
    municipality: 'North Vancouver',
    apiType: 'igolfsystems',
    backendId: 'seymour',
    bookingUrl: 'https://seymour.igolfsystems.net',
    supportedHoles: [18]
  },
  // Burnaby courses
  '1': {
    name: 'Burnaby Mountain Golf Course',
    municipality: 'burnaby',
    backendId: '1',
    bookingUrl: 'https://golfburnaby.cps.golf',
    supportedHoles: [18]
  },
  '2': {
    name: 'Riverway Golf Course',
    municipality: 'burnaby',
    backendId: '2',
    bookingUrl: 'https://golfburnaby.cps.golf',
    supportedHoles: [18]
  },
  // Vancouver courses
  '3': {
    name: 'Langara',
    municipality: 'vancouver',
    backendId: '1',
    bookingUrl: 'https://golfvancouver.cps.golf',
    supportedHoles: [18]
  },
  '4': {
    name: 'Fraserview Golf Course',
    municipality: 'vancouver',
    backendId: '2',
    bookingUrl: 'https://golfvancouver.cps.golf',
    supportedHoles: [18]
  },
  '5': {
    name: 'McCleery Golf Course',
    municipality: 'vancouver',
    backendId: '3',
    bookingUrl: 'https://golfvancouver.cps.golf',
    supportedHoles: [18]
  },
  // Gleneagles Golf Course
  '9': {
    name: 'Gleneagles Golf Course',
    municipality: 'West Vancouver',
    backendId: '1',
    bookingUrl: 'https://w.cps.golf/WestVancouverV3',
    supportedHoles: [9]
  },
  // Northlands Golf Course
  '7': {
    name: 'Northlands Golf Course',
    municipality: 'North Vancouver',
    backendId: '1',
    bookingUrl: 'https://northlands.cps.golf',
    supportedHoles: [18]
  },
  // University Golf Club
  '6': {
    name: 'University Golf Club',
    municipality: 'Vancouver',
    backendId: 'university',
    bookingUrl: 'https://universitygolf.com/',
    supportedHoles: [18]
  },
  // Sky Course - GreeTee Country Club
  '10': {
    name: 'Sky Course - GreenTee Country Club',
    municipality: 'Coquitlam (Westwood Plateau)',
    backendId: 'skyCourse',
    bookingUrl: 'https://www.gtccwestwoodplateau.com/golf/country-club/',
    supportedHoles: [18]
  },
  // Fine Course - GreenTee Country Club
  '11': {
    name: 'Fine Course - GreenTee Country Club',
    municipality: 'Coquitlam (Westwood Plateau)',
    backendId: 'fineCourse',
    bookingUrl: 'https://www.gtccwestwoodplateau.com/golf/executive-course/',
    supportedHoles: [9]
  },
  // Carnoustie Golf Club
  '12': {
    name: 'Carnoustie Golf Club',
    municipality: 'Port Coquitlam',
    backendId: 'carnoustie',
    bookingUrl: 'https://carnoustiegolfclub.ca/',
    supportedHoles: [18]
  },
  // Golden Eagle Golf Club - North Course
  '13': {
    name: 'Golden Eagle Golf Club - North Course',
    municipality: 'Pitt Meadows',
    backendId: 'genorthcourse',
    bookingUrl: 'https://www.goldeneaglegolfclub.com/copy-of-online-booking',
    supportedHoles: [18]
  },
  // Golden Eagle Golf Club - South Course
  '14': {
    name: 'Golden Eagle Golf Club - South Course',
    municipality: 'Pitt Meadows',
    backendId: 'gesouthcourse',
    bookingUrl: 'https://www.goldeneaglegolfclub.com/copy-of-online-booking',
    supportedHoles: [18]
  },
  '15': {
    name: 'Surrey Golf Club - Main Course',
    municipality: 'Surrey',
    backendId: 'surreygolfclub',
    bookingUrl: 'https://www.surreygolf.com/',
    supportedHoles: [18]
  },
  '16': {
    name: 'Surrey Golf Club - Willows 9',
    municipality: 'Surrey',
    backendId: 'surreywillows',
    bookingUrl: 'https://www.surreygolf.com/',
    supportedHoles: [9]
  },
  '17': {
    name: 'Savage Creek',
    municipality: 'Richmond',
    backendId: 'savagecreek',
    bookingUrl: 'https://www.savagecreekgolf.com/',
    supportedHoles: [16]
  },
  '18': {
    name: 'Mylora Golf Course',
    municipality: 'Richmond',
    backendId: 'mylora',
    bookingUrl: 'https://www.mylora.ca/',
    supportedHoles: [18]
  },
  '19': {
    name: 'Country Meadows',
    municipality: 'Richmond',
    backendId: 'countrymeadows',
    bookingUrl: 'https://www.countrymeadowsgolfcourse.ca/',
    supportedHoles: [18]
  },
  '20': {
    name: 'Furry Creek Golf and Country Club',
    municipality: 'Furry Creek',
    backendId: 'furrycreek',
    bookingUrl: 'https://www.furrycreekgolf.com/',
    supportedHoles: [18]
  },
  '21': {
    name: 'Meadow Gardens Golf Club',
    municipality: 'Pitt Meadows',
    backendId: 'meadowgardens',
    bookingUrl: 'https://meadowgardensgolf.com/',
    supportedHoles: [18]
  },
  '22': {
    name: 'Musqueam Golf & Learning Academy',
    municipality: 'Vancouver',
    backendId: 'musqueam',
    bookingUrl: 'https://www.musqueamgolf.com/',
    supportedHoles: [18]
  },
  '23': {
    name: 'The Hills at Portal Golf Club',
    municipality: 'White Rock',
    backendId: 'hillsatportal',
    bookingUrl: 'https://thehillsatportal.com',
    supportedHoles: [18]
  },
  '24': {
    name: 'Meridian Hills Golf Par 3',
    municipality: 'White Rock',
    backendId: 'meridianhills',
    bookingUrl: 'https://www.meridianhills.ca',
    supportedHoles: [18]
  },
  '25': {
    name: 'Harrison Resort Golf Course',
    municipality: 'Agassiz',
    backendId: 'harrisonresort',
    bookingUrl: 'http://www.harrisonresort.com/golf.aspx',
    supportedHoles: [9]
  },
  '26': {
    name: 'The Falls Golf Club',
    municipality: 'Chilliwack',
    backendId: 'thefalls',
    bookingUrl: 'https://www.thefalls.com/',
    supportedHoles: [18]
  },
  '27': {
    name: 'Tsawwassen Springs Golf',
    municipality: 'Delta',
    backendId: 'tsawwassensprings',
    bookingUrl: 'https://www.tsawwassensprings.ca/',
    supportedHoles: [18]
  },
  '28': {
    name: 'Sandpiper Resort Golf Course',
    municipality: 'Harrison Mills',
    backendId: 'sandpiperresort',
    bookingUrl: 'https://sandpiperresort.ca/golf/',
    supportedHoles: [18]
  },
  '29': {
    name: 'Hope Golf & Country Club',
    municipality: 'Hope',
    backendId: 'hopegolf',
    bookingUrl: 'https://hopegolfclub.com/',
    supportedHoles: [9]
  },
  '30': {
    name: 'GreenTee Country Club Langley',
    municipality: 'Langley',
    backendId: 'greenteelangley',
    bookingUrl: 'https://gtcclangley.com/',
    supportedHoles: [18]
  },
  '31': {
    name: 'Mount Brenton Golf Club',
    municipality: 'Chemainus',
    backendId: 'mountbrenton',
    bookingUrl: 'https://www.mountbrentongolf.com/',
    supportedHoles: [18]
  },
  '32': {
    name: 'Arbutus Ridge Golf Club',
    municipality: 'Cobble Hill',
    backendId: 'arbutusridge',
    bookingUrl: 'https://www.arbutusridge.com/',
    supportedHoles: [18]
  },
  '33': {
    name: 'The Park at Crown Isle',
    municipality: 'Comox',
    backendId: 'crownislepark',
    bookingUrl: 'https://crownisle.com/',
    supportedHoles: [18]
  },
  '34': {
    name: 'Crown Isle Resort and Golf Community',
    municipality: 'Courtenay',
    backendId: 'crownisleresort',
    bookingUrl: 'https://crownisle.com/',
    supportedHoles: [18]
  },
  '35': {
    name: 'Mulligans Golf Course',
    municipality: 'Courtenay',
    backendId: 'mulligans',
    bookingUrl: 'http://www.mulligansgolfcourse.com/',
    supportedHoles: [9]
  },
  '36': {
    name: 'Sunnydale Golf and Country Club',
    municipality: 'Courtenay',
    backendId: 'sunnydale',
    bookingUrl: 'https://sunnydalegolf.ca/',
    supportedHoles: [18]
  },
  '37': {
    name: 'Cowichan Golf & Country Club',
    municipality: 'Duncan',
    backendId: 'cowichan',
    bookingUrl: 'https://cowichangolfclub.ca/',
    supportedHoles: [18]
  },
  '38': {
    name: 'Duncan Meadows Golf Course',
    municipality: 'Duncan',
    backendId: 'duncanmeadows',
    bookingUrl: 'https://www.duncanmeadows.com/',
    supportedHoles: [18]
  },
  '39': {
    name: 'March Meadows Golf Club',
    municipality: 'Honeymoon Bay',
    backendId: 'marchmeadows',
    bookingUrl: 'https://marchmeadowsgolf.com/',
    supportedHoles: [9]
  },
  '40': {
    name: 'Morningstar Golf Club',
    municipality: 'Parksville',
    backendId: 'morningstar',
    bookingUrl: 'https://morningstargolf.com/',
    supportedHoles: [18]
  },
  '41': {
    name: 'Salt Spring Island Golf Club',
    municipality: 'Salt Spring Island',
    backendId: 'saltspring',
    bookingUrl: 'https://saltspringgolf.com/',
    supportedHoles: [9]
  },
  '42': {
    name: 'Bear Mountain Golf Resort - Mountain Course',
    municipality: 'Victoria',
    backendId: 'bearmountainmountain',
    bookingUrl: 'https://bearmountain.ca/',
    supportedHoles: [18]
  },
  '43': {
    name: 'Bear Mountain Golf Resort - Valley Course',
    municipality: 'Victoria',
    backendId: 'bearmountainvalley',
    bookingUrl: 'https://bearmountain.ca/',
    supportedHoles: [18]
  },
  '44': {
    name: 'Metchosin Golf Course',
    municipality: 'Victoria',
    backendId: 'metchosin',
    bookingUrl: 'https://metchosingolfcourse.com/',
    supportedHoles: [9]
  },
  '45': {
    name: 'Big Sky Golf Club',
    municipality: 'Pemberton',
    backendId: 'bigsky',
    bookingUrl: 'https://bigskygolf.ca/',
    supportedHoles: [18]
  },
  '46': {
    name: 'Fairmont Chateau Whistler Golf Club',
    municipality: 'Whistler',
    backendId: 'chateauwhistler',
    bookingUrl: 'https://www.whistlermountaingolf.com/',
    supportedHoles: [18]
  },
  '47': {
    name: 'Bighorn Golf & Country Club',
    municipality: 'Kamloops',
    backendId: 'bighorn',
    bookingUrl: 'https://golfbighorn.ca/',
    supportedHoles: [18]
  },
  '48': {
    name: 'Eaglepoint Golf Resort',
    municipality: 'Kamloops',
    backendId: 'eaglepoint',
    bookingUrl: 'https://www.eaglepointgolfresort.com/',
    supportedHoles: [18]
  },
  '49': {
    name: 'Kamloops Golf & Country Club',
    municipality: 'Kamloops',
    backendId: 'kamloopsgolf',
    bookingUrl: 'http://kamloopsgolfclub.com/',
    supportedHoles: [18]
  },
  '50': {
    name: 'Mount Paul Golf Course',
    municipality: 'Kamloops',
    backendId: 'mountpaul',
    bookingUrl: 'https://mountpaulgolf.com/',
    supportedHoles: [9]
  },
  '51': {
    name: 'Michaelbrook Golf',
    municipality: 'Kelowna',
    backendId: 'michaelbrook',
    bookingUrl: 'https://www.michaelbrook.ca/',
    supportedHoles: [18]
  },
  '52': {
    name: 'Mission Creek Golf Club',
    municipality: 'Kelowna',
    backendId: 'missioncreek',
    bookingUrl: 'https://www.missioncreekgolfclub.com/',
    supportedHoles: [18]
  },
  '53': {
    name: 'Sunset Ranch Golf and Country Club',
    municipality: 'Kelowna',
    backendId: 'sunsetranch',
    bookingUrl: 'https://sunsetranchbc.com/',
    supportedHoles: [18]
  },
  '54': {
    name: 'Tower Ranch Golf Club',
    municipality: 'Kelowna',
    backendId: 'towerranch',
    bookingUrl: 'https://www.golftowerranch.com/',
    supportedHoles: [18]
  },
  '55': {
    name: 'Merritt Golf & Country Club',
    municipality: 'Merritt',
    backendId: 'merrittgolf',
    bookingUrl: 'https://merrittgolf.com/',
    supportedHoles: [9]
  },
  '56': {
    name: 'Predator Ridge Resort',
    municipality: 'Vernon',
    backendId: 'predatorridge',
    bookingUrl: 'https://www.predatorridge.com/',
    supportedHoles: [18]
  },
  '57': {
    name: 'Spallumcheen Golf & Country Club',
    municipality: 'Vernon',
    backendId: 'spallumcheen',
    bookingUrl: 'https://spallumcheengolf.com/',
    supportedHoles: [18]
  },
  '58': {
    name: 'The Rise Resort',
    municipality: 'Vernon',
    backendId: 'theriseresort',
    bookingUrl: 'https://theriseresort.ca/',
    supportedHoles: [18]
  },
  '59': {
    name: "Tobiano Golf Course",
    municipality: 'Kamloops',
    backendId: 'tobiano',
    bookingUrl: 'https://tobianogolf.com/',
    supportedHoles: [18]
  },
  '60': {
    name: "Talking Rock Golf Course",
    municipality: 'Chase',
    backendId: 'talkingrock',
    bookingUrl: 'https://talkingrock.ca/',
    supportedHoles: [18]
  },
  '61': {
    name: "Sun Peaks Golf Course",
    municipality: 'Sun Peaks',
    backendId: 'sunpeaks',
    bookingUrl: 'https://www.sunpeaksresort.com/golf',
    supportedHoles: [18]
  },
  '62': {
    name: "Shuswap National Golf Course",
    municipality: 'Salmon Arm',
    backendId: 'shuswapnational',
    bookingUrl: 'https://shuswapnational.com/',
    supportedHoles: [18]
  },
  '63': {
    name: "Summerland Golf & Country Club",
    municipality: 'Summerland',
    backendId: 'summerland',
    bookingUrl: 'https://www.summerlandgolf.com/',
    supportedHoles: [18]
  },
  '64': {
    name: "Fairview Mountain Golf Club",
    municipality: 'Oliver',
    backendId: 'fairviewmountain',
    bookingUrl: 'https://fairviewmountain.com/',
    supportedHoles: [18]
  },
  '65': {
    name: "NK'Mip Canyon Desert Golf Course",
    municipality: 'Oliver',
    backendId: 'nkmipcanyon',
    bookingUrl: 'https://nkmipcanyon.com/',
    supportedHoles: [18]
  },
  '66': {
    name: "Osoyoos Golf Club - Desert Gold",
    municipality: 'Osoyoos',
    backendId: 'osoyoosdesertgold',
    bookingUrl: 'https://www.golfosoyoos.com/',
    supportedHoles: [18]
  },
  '67': {
    name: "Osoyoos Golf Club - Park Meadows",
    municipality: 'Osoyoos',
    backendId: 'osoyoosparkmeadows',
    bookingUrl: 'https://www.golfosoyoos.com/',
    supportedHoles: [18]
  },
  '68': {
    name: "Revelstoke Golf Club",
    municipality: 'Revelstoke',
    backendId: 'revelstoke',
    bookingUrl: 'https://www.golfrevelstoke.com/',
    supportedHoles: [18]
  },
  '69': {
    name: "Princeton Golf Club",
    municipality: 'Princeton',
    backendId: 'princeton',
    bookingUrl: 'https://www.princetongolfclub.com/',
    supportedHoles: [18]
  },
  '70': {
    name: "Storey Creek Golf Club",
    municipality: 'Campbell River',
    backendId: 'storeycreek',
    bookingUrl: 'https://storeycreek.bc.ca/',
    supportedHoles: [18]
  },
  '71': {
    name: "Qualicum Beach Memorial Golf Club",
    municipality: 'Qualicum Beach',
    backendId: 'qualicumbeach',
    bookingUrl: 'https://www.golfqualicum.ca/',
    supportedHoles: [9]
  },
  '72': {
    name: "Blue Ocean Golf Club",
    municipality: 'Sechelt',
    backendId: 'blueocean',
    bookingUrl: 'https://blueoceangolf.ca/',
    supportedHoles: [18]
  },
  '73': {
    name: "Cove Links Golf Course",
    municipality: 'Delta',
    backendId: 'covelinks',
    bookingUrl: 'http://covelinks.com/',
    supportedHoles: [9]
  },
  '74': {
    name: "Northview Golf & Country Club - Ridge Course",
    municipality: 'Surrey',
    backendId: '1',
    bookingUrl: 'https://northview.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '75': {
    name: "Northview Golf & Country Club - Canal Course",
    municipality: 'Surrey',
    backendId: '2',
    bookingUrl: 'https://northview.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '76': {
    name: "Morgan Creek Golf Course",
    municipality: 'Surrey',
    backendId: '2',
    bookingUrl: 'https://morgancreekbc.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '77': {
    name: "Delta Golf Club",
    municipality: 'Delta',
    backendId: '2',
    bookingUrl: 'https://delta.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '78': {
    name: "Kings Links by the Sea",
    municipality: 'Delta',
    backendId: '1',
    bookingUrl: 'https://kingslinks.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '79': {
    name: "Belmont Golf Course",
    municipality: 'Langley',
    backendId: '2',
    bookingUrl: 'https://westcoastgolfgroup.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '80': {
    name: "Swan-e-set Bay Resort - Resort Course",
    municipality: 'Pitt Meadows',
    backendId: '3',
    bookingUrl: 'https://westcoastgolfgroup.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '81': {
    name: "Swan-e-set Bay Resort - Links Course",
    municipality: 'Pitt Meadows',
    backendId: '4',
    bookingUrl: 'https://westcoastgolfgroup.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '82': {
    name: "Hazelmere Golf Course",
    municipality: 'Surrey',
    backendId: '1',
    bookingUrl: 'https://westcoastgolfgroup.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '83': {
    name: "The Redwoods Golf Course",
    municipality: 'Langley',
    backendId: '1',
    bookingUrl: 'https://redwoodsbc.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '84': {
    name: "Two Eagles Golf Course",
    municipality: 'West Kelowna',
    backendId: '2',
    bookingUrl: 'https://twoeagles.cps.golf/onlineresweb/search-teetime',
    supportedHoles: [18]
  },
  '85': {
    name: "Guildford Golf & Country Club",
    municipality: 'Surrey',
    backendId: 'guildford',
    bookingUrl: 'https://guildford-golf-and-cc.book.teeitup.com/',
    supportedHoles: [18]
  },
};

const SearchResults = () => {
  console.log('SearchResults component rendering');
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchId, setSearchId] = useState(null);
  const initialTimeRange = searchParams.get('timeRange') || 'all';
  const [selectedTimeRange, setSelectedTimeRange] = useState(initialTimeRange);
  const [selectedPriceRange, setSelectedPriceRange] = useState('200');
  const [userAgreed, setUserAgreed] = useState(false);
  const searchedCourseIds = searchParams.get('courses')?.split(',') || [];
  const [activeCourseIds, setActiveCourseIds] = useState(searchedCourseIds);
  // 'course' keeps the original grouping; 'time' flattens everything into one
  // chronological list across courses.
  const [sortBy, setSortBy] = useState('course');
  // Collapsed by default so results are the first thing on the page. With 85
  // courses the old inline chip grid pushed them below the fold entirely.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [courseFilterQuery, setCourseFilterQuery] = useState('');

  // Editing date or players rewrites the query string, which the fetch effect
  // already depends on, so the search re-runs automatically.
  const updateCriteria = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => next.set(k, String(v)));
    setSearchParams(next, { replace: true });
  };

  // Debug component mounting and URL parameters
  useEffect(() => {
    console.log('SearchResults component mounted');
    console.log('Current URL:', window.location.href);
    console.log('Current URL search params:', Object.fromEntries(searchParams.entries()));
  }, []);

  // Add function to convert 24hr to 12hr format
  const convertTo12Hour = (time24) => {
    // If the time already includes AM/PM, return it as is
    if (time24.includes('AM') || time24.includes('PM')) {
      return time24;
    }
    
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Filter results based on time range and price
  const applyFilters = (teeTimes, timeRange, maxPrice) => {
    let filtered = teeTimes;

    // Apply time range filter
    if (timeRange && timeRange !== 'all' && TIME_RANGES[timeRange]) {
      const { min, max } = TIME_RANGES[timeRange];
      filtered = filtered.filter(teeTime => {
        const time = teeTime.time.split(':')[0]; // Get hours
        const hour = parseInt(time);
        return hour >= parseInt(min) && hour < parseInt(max);
      });
    }

    // Apply price filter
    const priceValue = Number(maxPrice) || 0;
    if (maxPrice !== '') {
      filtered = filtered.filter(teeTime => {
        return Number(teeTime.price) <= priceValue;
      });
    }

    return filtered;
  };

  // Helper: Parse time string to minutes since midnight
  function parseTimeToMinutes(timeStr) {
    // If already in 24-hour format (e.g., 07:19 or 17:30)
    if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
      const [h, m] = timeStr.split(':').map(Number);
      return h * 60 + m;
    }
    // If in 12-hour format with AM/PM (e.g., 7:19 AM, 12:30 PM)
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
    if (match) {
      let hour = parseInt(match[1], 10);
      const min = parseInt(match[2], 10);
      const ampm = match[3].toUpperCase();
      if (ampm === 'PM' && hour !== 12) hour += 12;
      if (ampm === 'AM' && hour === 12) hour = 0;
      return hour * 60 + min;
    }
    // Fallback: try to extract numbers
    const nums = timeStr.match(/\d+/g);
    if (nums && nums.length >= 2) {
      const [h, m] = nums.map(Number);
      return h * 60 + m;
    }
    return 0; // fallback to midnight if unparseable
  }

  useEffect(() => {
    const fetchResults = async () => {
      try {
        console.log('Starting fetchResults');
        setLoading(true);
        setError(null);

        // Log the search parameters and API endpoint
        console.log('API Endpoint:', API_ENDPOINTS.searchTeeTimes);
        console.log('Search parameters:', {
          courses: searchParams.get('courses'),
          date: searchParams.get('date'),
          players: searchParams.get('players'),
          holes: searchParams.get('holes'),
          timeRange: searchParams.get('timeRange'),
          maxPrice: searchParams.get('maxPrice')
        });

        // First, make the search request
        console.log('Making API call to:', API_ENDPOINTS.searchTeeTimes);
        const searchResponse = await axios.get(API_ENDPOINTS.searchTeeTimes, {
          params: {
            courses: searchParams.get('courses'),
            date: searchParams.get('date'),
            players: searchParams.get('players'),
            holes: searchParams.get('holes'),
            timeRange: searchParams.get('timeRange')
          }
        });

        console.log('API Response:', searchResponse);

        if (searchResponse.data.success) {
          setSearchId(searchResponse.data.data.searchId);
          const allResults = searchResponse.data.data.results;
          setResults(allResults);
          
          // Filter results based on selected time range and price
          const filtered = applyFilters(allResults, selectedTimeRange, selectedPriceRange);
          setFilteredResults(filtered);
          
          setLoading(false);
        } else {
          setError(searchResponse.data.error || 'Failed to fetch tee times');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching results:', err);
        if (err.response) {
          console.error('Error response:', err.response.data);
          console.error('Error status:', err.response.status);
          console.error('Error headers:', err.response.headers);
        }
        setError(err.response?.data?.error || 'An error occurred while fetching tee times');
        setLoading(false);
      }
    };

    fetchResults();
  }, [searchParams]);

  // Add effect to update filtered results when filters change
  useEffect(() => {
    if (results.length > 0) {
      let filtered = applyFilters(results, selectedTimeRange, selectedPriceRange);
      if (activeCourseIds.length > 0) {
        // Debug log
        console.log('Filtering tee times:', filtered.map(tt => ({ course: tt.course, courseId: tt.courseId })), 'Active:', activeCourseIds);
        filtered = filtered.filter(tt => {
          const id = tt.courseId !== undefined ? String(tt.courseId) : String(tt.course);
          return activeCourseIds.includes(id);
        });
      }
      setFilteredResults(filtered);
    }
  }, [results, selectedTimeRange, selectedPriceRange, activeCourseIds]);

  useEffect(() => {
    if (!results || results.length === 0) {
      setFilteredResults([]);
      return;
    }

    // Get Vancouver time now
    const nowVancouver = DateTime.now().setZone('America/Vancouver');

    // Assume all results are for the same date as the first result
    const searchDate = results[0]?.date;
    const isToday = searchDate === nowVancouver.toISODate();

    const filtered = results.filter(result => {
      if (!isToday) return true;
      // result.time should be in 'HH:mm' or 'HH:mm:ss' format
      // Try both formats for robustness
      let teeTime = DateTime.fromFormat(result.time, 'HH:mm', { zone: 'America/Vancouver' });
      if (!teeTime.isValid) {
        teeTime = DateTime.fromFormat(result.time, 'HH:mm:ss', { zone: 'America/Vancouver' });
      }
      // Combine date and time for comparison
      const teeDateTime = DateTime.fromISO(searchDate, { zone: 'America/Vancouver' })
        .set({ hour: teeTime.hour, minute: teeTime.minute, second: teeTime.second });
      return teeDateTime > nowVancouver;
    });
    setFilteredResults(filtered);
  }, [results]);

  // Format date for display
  const formatDate = (dateString) => {
    // Split the date string and create a new Date object using components
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Format time range for display
  const formatTimeRange = (timeRange) => {
    const ranges = {
      morning: 'Morning (6:00 AM - 12:00 PM)',
      afternoon: 'Afternoon (12:00 PM - 6:00 PM)',
      evening: 'Evening (6:00 PM - 10:00 PM)'
    };
    return ranges[timeRange] || timeRange;
  };

  // Format course names for display
  const formatCourseNames = (coursesParam) => {
    if (!coursesParam) return 'All Courses';
    
    const courseIds = coursesParam.split(',');
    const courseNames = courseIds.map(id => {
      const course = COURSES[id];
      return course ? course.name : `Unknown Course (${id})`;
    });
    
    return courseNames.join(', ');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="w-full py-8 pt-20">
        <h1 className="text-5xl font-semibold tracking-tight text-center text-gray-900 sm:text-6xl mb-6 sm:mb-8 px-4 sm:px-0 text-left sm:text-center">Search Results</h1>
        <div className="w-full bg-gray-800 p-0 rounded-sm sm:rounded-lg sm:p-6 sm:max-w-3xl sm:mx-auto mb-6 pb-6">
          {/* Search Summary */}
          <h2 className="text-lg text-white font-semibold mb-3 px-4 pt-2 sm:pt-0">Search Details</h2>
          <div className="bg-gray-50 rounded-lg sm:p-4 p-0 mb-6 mx-2 sm:mx-0">
            <div className="px-4 pt-2 pb-2">
              <div className="flex gap-4 mb-2">
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="font-medium">{formatDate(searchParams.get('date'))}</p>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Players</p>
                  <p className="font-medium">{searchParams.get('players') || '2'} players</p>
                </div>
              </div>
              <div className="mb-2">
                <p className="text-sm text-gray-600">Selected Courses</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {searchedCourseIds.map(id => (
                    <button
                      key={id}
                      type="button"
                      className={`px-3 py-1 rounded-full border text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
                        ${activeCourseIds.includes(id)
                          ? 'bg-green-100 text-green-800 border-green-400 hover:bg-green-200'
                          : 'bg-gray-200 text-gray-400 border-gray-300 line-through hover:bg-gray-300'}`}
                      onClick={() => {
                        setActiveCourseIds(prev =>
                          prev.includes(id)
                            ? prev.filter(cid => cid !== id)
                            : [...prev, id]
                        );
                      }}
                    >
                      {COURSES[id]?.name || id}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="text-center py-8 px-4">
            <p className="text-gray-500 mb-4">No tee times found for the selected criteria.</p>
            <a
              href={`/alerts?courses=${encodeURIComponent(searchedCourseIds.join(','))}&date=${encodeURIComponent(
                searchParams.get('date') || ''
              )}&players=${encodeURIComponent(searchParams.get('players') || '2')}`}
              className="tiu-btn tiu-btn-lg tiu-btn-primary"
            >
              Alert me when one opens
            </a>
            <p className="text-gray-400 text-sm mt-3">
              We'll watch {searchedCourseIds.length === 1 ? 'this course' : `these ${searchedCourseIds.length} courses`} and
              email you the moment something matching appears — cancellations included.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Group filtered results by course and sort by time
  const filteredResultsByCourse = filteredResults.reduce((acc, teeTime) => {
    const key = String(teeTime.courseId !== undefined ? teeTime.courseId : teeTime.course);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(teeTime);
    return acc;
  }, {});

  // Sort tee times within each course by time (ascending)
  Object.keys(filteredResultsByCourse).forEach(courseId => {
    filteredResultsByCourse[courseId].sort((a, b) => {
      return parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time);
    });
  });

  // Get search parameters from URL
  const selectedCourseIds = searchParams.get('courses')?.split(',') || [];
  const date = searchParams.get('date') || new Date().toISOString().split('T')[0];
  const players = searchParams.get('players') || '2';
  const timeRange = searchParams.get('timeRange') || 'all';
  const maxPrice = parseInt(searchParams.get('maxPrice')) || 200;

  const chronological = [...filteredResults].sort(
    (a, b) => parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time)
  );

  const activeSearchedIds = searchedCourseIds.filter((id) => activeCourseIds.includes(id));

  const TeeTimeCard = ({ result, showCourse }) => (
    <div className="tiu-tee">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="tiu-tee-time">{convertTo12Hour(result.time)}</p>
          {showCourse && (
            <p className="tiu-tee-course" title={COURSES[result.courseId]?.name || result.courseId}>
              {COURSES[result.courseId]?.name || result.courseId}
            </p>
          )}
          <p className="tiu-tee-meta">
            {COURSES[result.courseId]?.supportedHoles
              ? `${COURSES[result.courseId].supportedHoles[0]} holes`
              : `${result.holes || 18} holes`}
          </p>
        </div>
        <span className="tiu-tee-price">${Number(result.price).toFixed(2)}</span>
      </div>
      <button
        onClick={() => window.open(COURSES[result.courseId]?.bookingUrl || '#', '_blank')}
        className="tiu-btn tiu-btn-primary w-full mt-3"
      >
        Book
      </button>
    </div>
  );

  return (
    <div className="px-4 pt-8 pb-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6">
          <h1 className="tiu-section-h">Available tee times</h1>
          <p className="tiu-muted mt-1">
            {filteredResults.length} {filteredResults.length === 1 ? 'time' : 'times'} on{' '}
            {formatDate(date)}
          </p>
        </div>

        {/* Compact summary + collapsible filters ------------------------- */}
        <div className="tiu-card mb-5">
          <div className="tiu-summary">
            <div className="tiu-summary-facts">
              <span className="tiu-fact"><strong>{formatDate(date)}</strong></span>
              <span className="tiu-fact-sep" />
              <span className="tiu-fact">{players} {players === '1' ? 'player' : 'players'}</span>
              <span className="tiu-fact-sep" />
              <span className="tiu-fact">
                {activeCourseIds.length} of {searchedCourseIds.length} courses
              </span>
              <span className="tiu-fact-sep" />
              <span className="tiu-fact">Max ${selectedPriceRange}</span>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="tiu-segment">
                <button
                  type="button"
                  aria-pressed={sortBy === 'course'}
                  onClick={() => setSortBy('course')}
                  className="tiu-segment-item"
                >
                  Course
                </button>
                <button
                  type="button"
                  aria-pressed={sortBy === 'time'}
                  onClick={() => setSortBy('time')}
                  className="tiu-segment-item"
                >
                  Time
                </button>
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                aria-expanded={filtersOpen}
                className="tiu-btn tiu-btn-secondary"
              >
                {filtersOpen ? 'Hide filters' : 'Filters'}
              </button>
            </div>
          </div>

          {filtersOpen && (
            <div className="border-t border-slate-200">
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="tiu-label" htmlFor="r-date">Date</label>
                  <input
                    id="r-date"
                    type="date"
                    value={date}
                    onChange={(e) => updateCriteria({ date: e.target.value })}
                    className="tiu-input"
                  />
                </div>

                <div>
                  <span className="tiu-label">Players</span>
                  <div className="tiu-segment w-full">
                    {['1', '2', '3', '4'].map((num) => (
                      <button
                        key={num}
                        type="button"
                        aria-pressed={players === num}
                        onClick={() => updateCriteria({ players: num })}
                        className="tiu-segment-item"
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="tiu-label" htmlFor="r-time">Time of day</label>
                  <select
                    id="r-time"
                    value={selectedTimeRange}
                    onChange={(e) => setSelectedTimeRange(e.target.value)}
                    className="tiu-input"
                  >
                    <option value="all">All times</option>
                    <option value="morning">Morning (6am-12pm)</option>
                    <option value="afternoon">Afternoon (12pm-6pm)</option>
                    <option value="evening">Evening (6pm-10pm)</option>
                  </select>
                </div>

                <div>
                  <label className="tiu-label" htmlFor="r-price">Max price</label>
                  <input
                    id="r-price"
                    type="number"
                    min="0"
                    step="10"
                    value={selectedPriceRange}
                    onChange={(e) => setSelectedPriceRange(e.target.value.replace(/^0+(?!$)/, ''))}
                    className="tiu-input"
                  />
                </div>
              </div>

              {/* Courses: a bounded, scrollable list rather than N inline chips */}
              <div className="px-5 pb-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="tiu-label mb-0">
                    Courses ({activeCourseIds.length}/{searchedCourseIds.length})
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="tiu-linkbtn"
                      onClick={() => setActiveCourseIds(searchedCourseIds)}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className="tiu-linkbtn"
                      onClick={() => setActiveCourseIds([])}
                    >
                      None
                    </button>
                  </div>
                </div>

                {searchedCourseIds.length > 8 && (
                  <input
                    type="text"
                    value={courseFilterQuery}
                    onChange={(e) => setCourseFilterQuery(e.target.value)}
                    placeholder="Find a course"
                    className="tiu-input mb-2"
                    aria-label="Find a course"
                  />
                )}

                <div className="tiu-scroll tiu-course-filter">
                  {searchedCourseIds
                    .filter((id) => {
                      const q = courseFilterQuery.trim().toLowerCase();
                      if (!q) return true;
                      const c = COURSES[id];
                      return (
                        (c?.name || id).toLowerCase().includes(q) ||
                        (c?.municipality || '').toLowerCase().includes(q)
                      );
                    })
                    .map((id) => {
                      const on = activeCourseIds.includes(id);
                      const count = (filteredResultsByCourse[String(id)] || []).length;
                      return (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={on}
                          onClick={() =>
                            setActiveCourseIds((prev) =>
                              prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
                            )
                          }
                          className={`tiu-option ${on ? 'is-selected' : ''}`}
                        >
                          <span className={`tiu-check ${on ? 'is-on' : ''}`} aria-hidden="true">
                            {on && (
                              <svg viewBox="0 0 12 10" fill="none">
                                <path d="M1 5.2 4.2 8.4 11 1.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="tiu-option-name">{COURSES[id]?.name || id}</span>
                          </span>
                          {count > 0 && <span className="tiu-badge flex-shrink-0">{count}</span>}
                        </button>
                      );
                    })}
                </div>
              </div>

              <div className="px-5 pb-5">
                <a href="/" className="tiu-btn tiu-btn-secondary w-full sm:w-auto">
                  Change course selection
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Results ------------------------------------------------------- */}
        {filteredResults.length === 0 ? (
          <div className="tiu-card p-10 text-center">
            <p className="tiu-h2">No tee times match your criteria</p>
            <a
              href={`/alerts?courses=${encodeURIComponent(searchedCourseIds.join(','))}&date=${encodeURIComponent(
                searchParams.get('date') || ''
              )}&players=${encodeURIComponent(searchParams.get('players') || '2')}`}
              className="tiu-btn tiu-btn-primary mt-4"
            >
              Alert me when one opens
            </a>
            <p className="tiu-muted mt-2">
              Try a different date, fewer players, or a higher price limit.
            </p>
          </div>
        ) : sortBy === 'time' ? (
          <div className="tiu-card p-4 sm:p-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {chronological.map((result) => (
                <TeeTimeCard
                  key={`${result.courseId}-${result.time}-${result.id}`}
                  result={result}
                  showCourse
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {activeSearchedIds.map((courseId) => {
              const teeTimes = filteredResultsByCourse[String(courseId)] || [];
              if (teeTimes.length === 0) return null;
              return (
                <section key={courseId} className="tiu-card overflow-hidden">
                  <div className="tiu-card-head">
                    <div>
                      <h2 className="tiu-h2">{COURSES[courseId]?.name || courseId}</h2>
                      <p className="tiu-muted">
                        {teeTimes.length} {teeTimes.length === 1 ? 'time' : 'times'} available
                      </p>
                    </div>
                    <span className="tiu-badge-green tiu-badge">
                      from ${Math.min(...teeTimes.map((t) => Number(t.price))).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-4 sm:p-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {teeTimes.map((result) => (
                      <TeeTimeCard
                        key={`${result.courseId}-${result.time}-${result.id}`}
                        result={result}
                        showCourse={false}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults; 